/**
 * Shows that a product list is loading while WooCommerce fetches its new order.
 *
 * Changing the sort order, or turning a page, swaps the products in place
 * without reloading (the Product Collection is an Interactivity API router
 * region), and the swap takes a moment: until then the old list sat there
 * unchanged, which read as the click having done nothing (asked 2026-10-05).
 *
 * The mark goes on the list's parent rather than the list: the list is the
 * region that gets replaced, and a class on it would be replaced with it. It
 * comes off when anything under the parent changes -- the products, or the
 * result count beside them, which the same navigation updates -- or after a
 * while regardless, so a failed request never leaves the list faded out.
 */

const LOADING_CLASS = 'is-loading-products';
const COLLECTION = '.wp-block-woocommerce-product-collection';
const GIVE_UP_AFTER = 15000;

function markLoading(collection) {
	const parent = collection?.parentElement;

	if (!parent || parent.classList.contains(LOADING_CLASS)) {
		return;
	}

	parent.classList.add(LOADING_CLASS);
	collection.setAttribute('aria-busy', 'true');

	let timer = null;

	const observer = new window.MutationObserver(() => {
		parent.classList.remove(LOADING_CLASS);
		parent.querySelector(COLLECTION)?.removeAttribute('aria-busy');
		observer.disconnect();
		clearTimeout(timer);
	});

	observer.observe(parent, {
		childList: true,
		subtree: true,
		characterData: true,
	});

	timer = setTimeout(() => {
		parent.classList.remove(LOADING_CLASS);
		parent.querySelector(COLLECTION)?.removeAttribute('aria-busy');
		observer.disconnect();
	}, GIVE_UP_AFTER);
}

export function initProductListLoading() {
	// Delegated: the sort select and the page links are re-rendered with the
	// regions they sit in, so listeners on the elements themselves would be
	// lost after the first navigation. And in the capture phase: WooCommerce's
	// own handlers stop the events there, so they never bubble up to the
	// document (found 2026-10-05).
	document.addEventListener(
		'change',
		(event) => {
			if (!event.target.matches('.wc-block-catalog-sorting select')) {
				return;
			}

			// The select sits beside the list, not in it: the list it orders
			// is the page's first one.
			markLoading(document.querySelector(COLLECTION));
		},
		true
	);

	document.addEventListener(
		'click',
		(event) => {
			const link = event.target.closest(
				`${COLLECTION} .wp-block-query-pagination a`
			);

			if (link) {
				markLoading(link.closest(COLLECTION));
			}
		},
		true
	);
}
